import { toast } from 'sonner';
import type { CompanyInfo, InvoiceDetail } from '@/types';
import { invoicePdfFilename } from '@/features/invoices/pdf';

/**
 * The invoice PDF, built in the browser with web-v2's own renderer (jspdf),
 * loaded only when someone asks for it.
 */
async function build(invoice: InvoiceDetail, company: CompanyInfo | undefined, clientLabel: string) {
  const { renderInvoicePdf } = await import('@/features/invoices/invoicePdf');
  return renderInvoicePdf({ invoice, company, clientLabel });
}

/** Open it full screen. The iOS shell shows blob URLs in its web view; back swipe returns. */
export async function viewInvoicePdf(invoice: InvoiceDetail, company: CompanyInfo | undefined, clientLabel: string) {
  try {
    const blob = await build(invoice, company, clientLabel);
    window.location.href = URL.createObjectURL(blob);
  } catch {
    toast.error('Could not build the PDF.');
  }
}

/** The share sheet with the PDF attached, or the full-screen view where sharing files is not supported. */
export async function shareInvoicePdf(invoice: InvoiceDetail, company: CompanyInfo | undefined, clientLabel: string) {
  let blob: Blob;
  try {
    blob = await build(invoice, company, clientLabel);
  } catch {
    toast.error('Could not build the PDF.');
    return;
  }
  const file = new File([blob], invoicePdfFilename(invoice), { type: 'application/pdf' });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: `Invoice ${invoice.invoiceNumber}` });
    } catch {
      // Dismissing the share sheet rejects; nothing to say.
    }
    return;
  }
  window.location.href = URL.createObjectURL(blob);
}
